import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Chip,
  CircularProgress,
  Alert,
  Slider,
  Paper,
} from '@mui/material';
import { AutoAwesome, Psychology } from '@mui/icons-material';

interface AIQuestionGeneratorProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (config: GenerationConfig) => Promise<void>;
  unitTitle: string;
}

export interface GenerationConfig {
  question_count: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'mixed';
  question_types: (
    | 'multiple_choice'
    | 'true_false'
    | 'word_problem'
    | 'numeric'
    | 'short_answer'
  )[];
  include_word_problems: boolean;
  word_problem_contexts: string[];
  customization_prompt: string;
  set_title: string;
  set_description: string;
}

const WORD_PROBLEM_CONTEXTS = [
  'Shopping & Money',
  'Sports & Games',
  'Cooking & Recipes',
  'Travel & Transportation',
  'Science Experiments',
  'Construction & Building',
  'Gardening & Nature',
  'Technology & Computers',
];

const steps = [
  'Question Count & Difficulty',
  'Question Types',
  'Word Problems',
  'Customization',
  'Review',
];

export const AIQuestionGenerator: React.FC<AIQuestionGeneratorProps> = ({
  open,
  onClose,
  onGenerate,
  unitTitle,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Count & Difficulty
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | 'mixed'>('mixed');

  // Step 2: Question Types
  const [questionTypes, setQuestionTypes] = useState<Set<string>>(
    new Set(['multiple_choice', 'true_false', 'word_problem']),
  );

  // Step 3: Word Problems
  const [includeWordProblems, setIncludeWordProblems] = useState(true);
  const [wordProblemContexts, setWordProblemContexts] = useState<Set<string>>(
    new Set(['Shopping & Money', 'Sports & Games']),
  );

  // Step 4: Customization
  const [setTitle, setSetTitle] = useState(`Practice Questions - ${unitTitle}`);
  const [setDescription, setSetDescription] = useState('AI-generated practice questions');
  const [customizationPrompt, setCustomizationPrompt] = useState('');

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleQuestionTypeToggle = (type: string) => {
    const newTypes = new Set(questionTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setQuestionTypes(newTypes);

    // Auto-update word problems flag
    if (type === 'word_problem') {
      setIncludeWordProblems(newTypes.has('word_problem'));
    }
  };

  const handleContextToggle = (context: string) => {
    const newContexts = new Set(wordProblemContexts);
    if (newContexts.has(context)) {
      newContexts.delete(context);
    } else {
      newContexts.add(context);
    }
    setWordProblemContexts(newContexts);
  };

  const handleGenerate = async () => {
    if (questionTypes.size === 0) {
      setError('Please select at least one question type');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const config: GenerationConfig = {
        question_count: questionCount,
        difficulty,
        question_types: Array.from(questionTypes) as any,
        include_word_problems: includeWordProblems,
        word_problem_contexts: Array.from(wordProblemContexts),
        customization_prompt: customizationPrompt,
        set_title: setTitle,
        set_description: setDescription,
      };

      await onGenerate(config);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setError(null);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              How many questions do you want?
            </Typography>
            <Box sx={{ px: 2, mb: 4 }}>
              <Slider
                value={questionCount}
                onChange={(_, value) => setQuestionCount(value as number)}
                min={5}
                max={30}
                step={5}
                marks
                valueLabelDisplay="on"
              />
              <Typography variant="caption" color="text.secondary">
                Recommended: 10-15 questions for effective practice
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Difficulty Level
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup value={difficulty} onChange={(e) => setDifficulty(e.target.value as any)}>
                <FormControlLabel value="easy" control={<Radio />} label="Easy - Basic concepts" />
                <FormControlLabel
                  value="medium"
                  control={<Radio />}
                  label="Medium - Standard difficulty"
                />
                <FormControlLabel
                  value="hard"
                  control={<Radio />}
                  label="Hard - Challenging problems"
                />
                <FormControlLabel
                  value="mixed"
                  control={<Radio />}
                  label="Mixed - Variety of difficulties (40% easy, 40% medium, 20% hard)"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Question Types
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose the types of questions you want to generate
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={questionTypes.has('multiple_choice')}
                    onChange={() => handleQuestionTypeToggle('multiple_choice')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Multiple Choice</Typography>
                    <Typography variant="caption" color="text.secondary">
                      4 options with one correct answer
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={questionTypes.has('true_false')}
                    onChange={() => handleQuestionTypeToggle('true_false')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">True/False</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Thought-provoking statements
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={questionTypes.has('word_problem')}
                    onChange={() => handleQuestionTypeToggle('word_problem')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Word Problems</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-world scenarios and applications
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={questionTypes.has('numeric')}
                    onChange={() => handleQuestionTypeToggle('numeric')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Numeric/Calculation</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Math problems requiring numerical answers
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={questionTypes.has('short_answer')}
                    onChange={() => handleQuestionTypeToggle('short_answer')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Short Answer</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Brief written responses
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>

            {questionTypes.size === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please select at least one question type
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Word Problem Settings
            </Typography>

            {!questionTypes.has('word_problem') ? (
              <Alert severity="info">Word problems are not selected. Go back to enable them.</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose real-world contexts for word problems
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WORD_PROBLEM_CONTEXTS.map((context) => (
                    <Chip
                      key={context}
                      label={context}
                      onClick={() => handleContextToggle(context)}
                      color={wordProblemContexts.has(context) ? 'primary' : 'default'}
                      variant={wordProblemContexts.has(context) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>

                {wordProblemContexts.size === 0 && questionTypes.has('word_problem') && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please select at least one context for word problems
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Customize Your Practice Set
            </Typography>

            <TextField
              fullWidth
              label="Practice Set Title"
              value={setTitle}
              onChange={(e) => setSetTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={setDescription}
              onChange={(e) => setSetDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" gutterBottom>
              Additional Instructions for AI (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Provide specific guidance to customize the questions
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="e.g., Focus on fractions and decimals, Include real-world examples from daily life, Make questions engaging for middle school students"
              value={customizationPrompt}
              onChange={(e) => setCustomizationPrompt(e.target.value)}
            />
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Configuration
            </Typography>

            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <AutoAwesome sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Generation Settings
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  <strong>Questions:</strong> {questionCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Difficulty:</strong>{' '}
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </Typography>
                <Typography variant="body2">
                  <strong>Types:</strong> {Array.from(questionTypes).join(', ')}
                </Typography>
                {questionTypes.has('word_problem') && (
                  <Typography variant="body2">
                    <strong>Word Problem Contexts:</strong>{' '}
                    {Array.from(wordProblemContexts).join(', ')}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Practice Set Details
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  <strong>Title:</strong> {setTitle}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {setDescription || 'None'}
                </Typography>
                {customizationPrompt && (
                  <Typography variant="body2">
                    <strong>Custom Instructions:</strong> {customizationPrompt}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <Psychology sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                AI will generate {questionCount} high-quality practice questions based on your unit
                content. This may take 30-60 seconds.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="primary" />
          <span>Generate Practice Questions with AI</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={generating}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 1 && questionTypes.size === 0) ||
              (activeStep === 2 &&
                questionTypes.has('word_problem') &&
                wordProblemContexts.size === 0)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || questionTypes.size === 0}
            startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesome />}
          >
            {generating ? 'Generating...' : 'Generate Questions'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
